// validate.ts mirrors internal/validate/keys.go. CLAUDE.md §8 pins the rules.
// The length caps + key charset are now GENERATED from internal/validate
// (MAX_KEY_LEN / MAX_PREFIX_LEN / KEY_RE, imported below) and CI-gated, so they
// can't drift from the server (cross-boundary-audit Slice 6); the handle GUID
// format below is a fixed standard kept local.
// The smoke-test glow (sdk/test) covers parity end-to-end.
//
//   Key:    1-256 bytes, RFC 3986 unreserved (alphanumerics + '-' '.' '_' '~')
//   Prefix: 0-256 bytes, same charset, empty allowed
//   Handle: 8-4-4-4-12 lower-hex GUID, exactly 36 chars

import { BacklitError } from "./errors.js";
import { MAX_KEY_LEN, MAX_PREFIX_LEN, KEY_RE } from "./validate-constants.gen.js";

const HANDLE_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

export function validateDataKey(key: unknown): asserts key is string {
  if (typeof key !== "string") {
    throw new BacklitError("invalid_key", "key must be a string");
  }
  if (key.length === 0) {
    throw new BacklitError("invalid_key", "key is empty");
  }
  // Server's MaxKeyLen is bytes; for ASCII-only charset, byte length
  // equals code-unit length. The KEY_RE below rejects anything outside
  // that charset, so this length check is also a byte cap.
  if (key.length > MAX_KEY_LEN) {
    throw new BacklitError("invalid_key", `key exceeds ${MAX_KEY_LEN} bytes`);
  }
  if (!KEY_RE.test(key)) {
    throw new BacklitError(
      "invalid_key",
      "key may only contain alphanumerics, '-', '.', '_', and '~'",
    );
  }
}

export function validateDataPrefix(prefix: unknown): asserts prefix is string {
  if (typeof prefix !== "string") {
    throw new BacklitError("invalid_prefix", "prefix must be a string");
  }
  if (prefix.length === 0) {
    // Empty prefix is valid — server treats it as "list everything in the
    // silo".
    return;
  }
  if (prefix.length > MAX_PREFIX_LEN) {
    throw new BacklitError(
      "invalid_prefix",
      `prefix exceeds ${MAX_PREFIX_LEN} bytes`,
    );
  }
  if (!KEY_RE.test(prefix)) {
    throw new BacklitError(
      "invalid_prefix",
      "prefix may only contain alphanumerics, '-', '.', '_', and '~'",
    );
  }
}

// validateOwnerUid is the minimal client-side guard for records.reassign's
// newOwnerUid: a non-empty string. The uid is opaque/server-issued (a stable
// per-user GUID, or an email hash for pattern-matched users), so we
// deliberately do NOT apply the data-key charset — the server does the
// authoritative shape validation (CLAUDE.md §9, D13). An empty or non-string
// value throws invalid_argument before any network call.
export function validateOwnerUid(uid: unknown): asserts uid is string {
  if (typeof uid !== "string" || uid.length === 0) {
    throw new BacklitError("invalid_argument", "newOwnerUid must be a non-empty string");
  }
}

// GUID_LEN is the fixed length of a bare 8-4-4-4-12 lower-hex GUID.
const GUID_LEN = 36;

// validateHandle accepts a capture handle in the form
// capture.create returns: "{key}-{guid}" (mirrors
// internal/validate/keys.go:ValidateHandle). The {guid} is the trailing
// 36-char GUID, separated from the {key} by '-'; the {key} matches the
// data-key charset. A bare GUID with no key prefix is rejected — the
// handle contract is the whole string create returned.
export function validateHandle(handle: unknown): asserts handle is string {
  if (typeof handle !== "string") {
    throw new BacklitError("invalid_handle", "handle must be a string");
  }
  // "{key}-{guid}": the GUID is the trailing 36 characters, preceded by a
  // '-' separating it from the key.
  if (handle.length < GUID_LEN + 2) {
    throw new BacklitError(
      "invalid_handle",
      `handle must be {key}-{guid}, got ${handle.length} characters`,
    );
  }
  const guid = handle.slice(handle.length - GUID_LEN);
  if (handle[handle.length - GUID_LEN - 1] !== "-") {
    throw new BacklitError(
      "invalid_handle",
      "handle must be {key}-{guid}",
    );
  }
  if (!HANDLE_RE.test(guid)) {
    throw new BacklitError(
      "invalid_handle",
      "the GUID segment of the handle must be a lower-hex 8-4-4-4-12 GUID",
    );
  }
  const key = handle.slice(0, handle.length - GUID_LEN - 1);
  if (key.length > MAX_KEY_LEN || !KEY_RE.test(key)) {
    throw new BacklitError(
      "invalid_handle",
      "the key segment of the handle is not a valid key",
    );
  }
}
